import { useVoidACHDisbursementMutation } from 'API/order/escrow/AchDisbursementAPI';
import React, { useCallback } from 'react';
import { useSelectCurrentOrderId } from 'selectors/order/selectCurrentOrder';
import { IACHDisbursementFormData } from 'types/order/escrow/achDisbursements/IACHDisbursementFormData';
import { assertDefined } from 'utilities/Assertions';
import { IVoidData } from './IVoidData';
import { checkForMutationSuccess } from 'API/checkForMutationSuccess';
import { voidACHSchema } from './schemas';
import { Flex, Alert } from '@zillow/constellation';
import { VoidConfirmationModal } from './VoidConfirmationModal';
import { VoidReason } from './VoidReason';
import { VoidACHDetails } from './VoidACHDetails';
import { IAchPushDisbursementData } from 'types/order/escrow/IAchPushDisbursementData';
import { ePermissions } from 'types/administration/userAdministration/ePermissions';
import { Confirmation } from './Confirmation';
import { eVoidReason, voidReasonOptions } from 'types/accounting/eVoidReason';
import { useHasPermissions } from 'components/accessControl/hooks/useHasPermissions';

interface IVoidACHButtonProps {
  achDisbursement: IACHDisbursementFormData | IAchPushDisbursementData;
}

export const VoidACHButton: React.FC<IVoidACHButtonProps> = ({ achDisbursement }) => {
  const orderId = useSelectCurrentOrderId();
  assertDefined(orderId);

  const [onVoidACH] = useVoidACHDisbursementMutation();

  const onVoid = useCallback(
    async (values: IVoidData) => {
      const result = await onVoidACH({
        orderId,
        ach: {
          id: achDisbursement.id,
          void_reason: values.voidReason as eVoidReason,
          void_reason_description:
            values.voidReason === eVoidReason.Other || values.voidReason === eVoidReason.SentInError
              ? values.voidReasonDescription
              : undefined,
        },
      });
      checkForMutationSuccess(result, 'voiding ACH');
    },
    [orderId, achDisbursement.id, onVoidACH]
  );

  const isVoidable = !achDisbursement.voidedAt && achDisbursement.sentAt;
  const hasPermissions = useHasPermissions([ePermissions.EditEscrowActualAchPushDepositsDisbursed]);
  if (!hasPermissions || !isVoidable) {
    return null;
  }

  return (
    <VoidConfirmationModal
      onVoid={onVoid}
      button-data-test-id={`void-ach-button-${achDisbursement.id}`}
      whatAreWeVoiding="ACH"
      initialValues={{
        checkInHand: false,
        confirmation: false,
        voidReason: '',
        voidReasonDescription: '',
      }}
      validationSchema={voidACHSchema}
      details={<VoidACHDetails achDisbursement={achDisbursement} />}
      form={
        <Flex display={'flex'} flexDirection={'column'} gap={1}>
          <VoidReason reasonList={voidReasonOptions} />
          <Confirmation />
          <Alert
            appearance="warning"
            body={`Only Void an ACH if you've confirmed that the payment was unsuccessful in Wells Fargo Payment Manager. Voiding an ACH WILL NOT reverse a completed payment.`}
          ></Alert>
        </Flex>
      }
    />
  );
};
